import { Select } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Organization } from 'protocol/model/dto_organization_pb';

import { useDebounce } from '@/shared/hooks';

import { OrganizationListFilter, organizationsMessages } from '../../config';
import { useOrganizationListQuery } from '../../hooks';

type OrganizationOption = {
  label: string;
  organization: Organization;
  value: string;
};

type OrganizationSearchSelectProps = {
  className?: string;
  disabled?: boolean;
  filter?: OrganizationListFilter;
  placeholder?: string;
  // The organization matching `value`, when the parent already holds it. Lets a
  // value set from outside render as a label instead of a bare id.
  selectedOrganization?: Organization;
  value?: string;
  onChange?: (organizationID?: string, organization?: Organization) => void;
};

const getOrganizationOption = (
  organization: Organization,
): OrganizationOption => {
  const name = organization.Name || organization.ID;

  return {
    label: organization.OwnerEmail
      ? `${name} (${organization.OwnerEmail})`
      : name,
    organization,
    value: organization.ID,
  };
};

export const OrganizationSearchSelect: FC<OrganizationSearchSelectProps> = (
  props,
) => {
  const {
    className,
    disabled,
    filter,
    placeholder,
    selectedOrganization,
    value,
    onChange,
  } = props;

  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The picked organization can fall outside the current page, so it is kept
  // aside to stay rendered as a label instead of a bare id.
  const [pickedOption, setPickedOption] = useState<OrganizationOption>();

  const debouncedSearch = useDebounce(search);

  const { hasError, loading, organizations } = useOrganizationListQuery({
    enabled: isOpen,
    filter,
    search: debouncedSearch,
  });

  const selectedOption = useMemo(() => {
    if (pickedOption?.value === value) {
      return pickedOption;
    }

    if (selectedOrganization && selectedOrganization.ID === value) {
      return getOrganizationOption(selectedOrganization);
    }

    return undefined;
  }, [pickedOption, selectedOrganization, value]);

  const options = useMemo(() => {
    const loadedOptions = organizations.map(getOrganizationOption);

    if (
      selectedOption &&
      !loadedOptions.some((option) => option.value === selectedOption.value)
    ) {
      return [selectedOption, ...loadedOptions];
    }

    return loadedOptions;
  }, [organizations, selectedOption]);

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      setSearch('');
    }
  };

  const handleChange = (nextValue?: string) => {
    const nextOption = options.find((option) => option.value === nextValue);

    setPickedOption(nextOption);
    onChange?.(nextValue, nextOption?.organization);
  };

  return (
    <Select<string>
      allowClear
      showSearch
      className={className}
      disabled={disabled}
      filterOption={false}
      loading={loading}
      notFoundContent={
        hasError ? formatMessage(organizationsMessages.error) : undefined
      }
      options={options}
      placeholder={placeholder}
      searchValue={search}
      value={value}
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onSearch={setSearch}
    />
  );
};
